import React, { useContext, useEffect, useState } from 'react';
import 'twin.macro';
import { useDataLoader, useUnmounted } from '../common/hooks';
import { Helmet } from 'react-helmet-async';
import { getItemsList } from 'services/dumpert';
import { getUnmoderatedCommentsList } from 'services/comments';
import { Item } from '../item';
import { Wrapper } from '../wrapper';
import { Carousel } from '../carousel';
import { ItemsGrid } from './';
import { Title } from '../text';
import { Ads, LazyloadAds, FluidAds } from '../ads';
import { AppContext, PreferenceContext, UserContext } from '../context';
import { removeEmpty } from '../common/o';
import { settings } from '../../settings';

export const ItemsListNew = () => {
  const newItems = 0;

  return (
    <>
      {newItems > 0 && (
        <>
          <Helmet>
            <title>{`(${newItems}) Dumpert.nl`}</title>
          </Helmet>
          Er zijn {newItems} nieuwe items toegevoegd.
        </>
      )}
    </>
  );
};

const NoResults = ({ confused = false }) => (
  <>
    <Title Type='h2' tw='mb-2 text-center'>
      {confused ? 'Geen' : 'einde van de'} resulaten
    </Title>
    <div tw='aspect-w-16 aspect-h-9'>
      <img
        src={
          confused
            ? 'https://assets.dumpert.nl/confused.gif'
            : 'https://assets.dumpert.nl/onvoldoende.gif'
        }
        tw='rounded-lg'
      />
    </div>
  </>
);

const adTypes = {
  TABLET: 'billboard',
  DESKTOP: 'billboardrectangle',
};

const shouldUseCFImageOptimization = (listType, page, sorting, period, searchTag) => {
  if (settings.USE_CF_IMGOPT) {
    const isValidSorting = sorting === 'date' || sorting === 'date%2B' || sorting === 'date-';
    switch (listType) {
      case 'latest':
        if (page === 0 && isValidSorting) {
          return true;
        }
        break;
      case 'toppers':
        if (
          (period === 'dag' || period === 'week' || period === '') &&
          isValidSorting &&
          page === 0
        ) {
          return true;
        }
        break;
      case 'search':
        if (searchTag === 'dumperttv' && isValidSorting && page === 0) {
          return true;
        }
        break;
      case 'tag':
        if (searchTag === 'dumperttv' && isValidSorting && page === 0) {
          return true;
        }
        break;
      default:
        return false;
    }
  }

  return false;
};

export const ItemsList = ({
  landscape,
  listType = 'latest',
  period = '',
  searchTag = '',
  filter = '',
  sorting = 'date',
  page = 0,
  date,
  maxNumItems = -1,
  WrapperType = undefined,
  itemId = undefined,
  hideLoadMore,
  isSidebar,
  hideAds = false,
}) => {
  const loadItemsList = useDataLoader(getItemsList);

  const { windowSize } = useContext(AppContext);
  const [data, setData] = useState([]);
  const { user } = useContext(UserContext);
  const {
    preference: { nsfwActive },
  } = useContext(PreferenceContext);
  const showNsfwItems = nsfwActive ? { nsfwActive } : {};
  const isToppers = listType === 'toppers';
  const isDumpertTv = searchTag === 'dumperttv';
  const isSearchOrTag = listType === 'search' || listType === 'tag';
  const { result, error } = loadItemsList(
    removeEmpty({
      list: listType,
      filter,
      page,
      tag: searchTag,
      period,
      sorting: isSearchOrTag ? (isDumpertTv ? 'date' : sorting) : undefined,
      itemId: listType === 'related' ? itemId : undefined,
      date,
      ...showNsfwItems,
    }),
  );

  const itemslist = result ? result.data.items : [];
  const isCarousel = WrapperType === Carousel;

  const unmounted = useUnmounted();
  const useCF = shouldUseCFImageOptimization(listType, page, sorting, period, searchTag);

  useEffect(() => {
    if (!error && result && itemslist && itemslist.length > 0 && user && user.moderate) {
      const itemsListIds = itemslist.map((i) => Number(i.id.split('_')[0]));
      getUnmoderatedCommentsList(itemsListIds)
        .then((item) => {
          if (!unmounted.current) {
            setData(item.data);
          }
        })
        .catch(() => {
          /* no-op */
        });
    } else {
      setData([]);
    }
  }, [user]);

  return (
    <>
      <ItemsListNew />
      {(error || result.data.errors) && <></>}
      {!error && !itemslist && <NoResults confused />}
      {!error && itemslist && (
        <>
          {itemslist && itemslist.length === 0 && (
            <NoResults confused={page === 0 && itemslist.length === 0} />
          )}
          {result && itemslist.length > 0 && (
            <ItemsGrid noGrid={isSidebar} page={page} hideLoadMore={hideLoadMore}>
              <Wrapper WrapperType={WrapperType} showDots={isCarousel}>
                {[
                  ...(itemId
                    ? itemslist.filter((i) => i.id !== itemId)
                    : isCarousel
                      ? itemslist.filter((i) => i.nsfw !== true)
                      : itemslist),
                ]
                  .slice(0, maxNumItems)
                  .map((item, index) => {
                    return (
                      <React.Fragment key={item.id}>
                        {isCarousel && <Item item={item} useCF={useCF} sliderItem />}
                        <div tw='relative'>
                          {data &&
                            data.length > 0 &&
                            user &&
                            user.moderate &&
                            data.map(
                              (mod) =>
                                mod.id === Number(item.id.split('_')[0]) &&
                                mod.unmoderated_comments > 0 && (
                                  <div
                                    key={mod.id}
                                    tw='bg-[red] z-20 absolute -ml-2 -mt-2 rounded-full flex items-center
                        justify-center w-6 h-6 font-semibold text-xs text-white'
                                  >
                                    {mod.unmoderated_comments}
                                  </div>
                                ),
                            )}
                          {!isCarousel && (
                            <Item
                              useCF={useCF}
                              item={item}
                              landscape={landscape}
                              index={index}
                              period={period}
                              page={page}
                              isTopper={isToppers}
                              isSidebar={isSidebar}
                            />
                          )}
                          {index + 1 === 3 &&
                            !hideAds &&
                            ['MOBILE'].includes(windowSize.adBreakPoint) && (
                              <Ads
                                Type={FluidAds}
                                adType='native'
                                adNumber='a1'
                                fluidAd
                                tw='m-0 p-0'
                              />
                            )}
                        </div>
                        {(index + 1) % 5 === 0 &&
                          !hideAds &&
                          ['MOBILE'].includes(windowSize.adBreakPoint) && (
                            <div tw='m-0'>
                              <Ads
                                Type={LazyloadAds}
                                adType='interscroller'
                                adNumber={`b${(index + 1) / 5 + page * 4}`}
                              />
                            </div>
                          )}
                      </React.Fragment>
                    );
                  })}
              </Wrapper>
            </ItemsGrid>
          )}
          {!isSidebar && hideAds && !['MOBILE'].includes(windowSize.adBreakPoint) && (
            <hr tw='my-8 border-icons opacity-30' />
          )}
          {!isSidebar && !hideAds && !['MOBILE'].includes(windowSize.adBreakPoint) && (
            <div tw='m-0 py-4 min-h-[250px]'>
              <Ads
                Type={LazyloadAds}
                adType={adTypes[windowSize.adBreakPoint]}
                adNumber={`b${page + 2}`}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
